import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const imageUrl = articleImages[article.slug] || articleImages['ai-2026-ren-gong-zhi-neng-zhan-wang'];

  if (variant === 'featured') {
    return (
      <Link
        to={'/article/' + article.slug}
        className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-100"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4 md:p-6">
          <div className="relative w-full aspect-[16/10] rounded-lg overflow-hidden bg-background-200">
            <img
              src={imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute top-3 left-3">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-500 text-white rounded-full whitespace-nowrap">
                {article.categoryName}
              </span>
            </div>
          </div>
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime} 分钟阅读</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span className="flex items-center gap-1"><i className="ri-eye-line text-xs"></i> {article.viewCount.toLocaleString()}</span>
            </div>
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3 group-hover:text-primary-500 transition-colors duration-200">
              {article.title}
            </h3>
            <p className="text-foreground-600 leading-relaxed line-clamp-3 mb-4">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
              </div>
              <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={'/article/' + article.slug}
        className="group flex items-start gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <div className="flex-shrink-0 w-20 h-20 rounded-md overflow-hidden bg-background-200">
          <img
            src={imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={'/article/' + article.slug}
      className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-100"
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-200">
        <img
          src={imageUrl}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-500 text-white rounded-full whitespace-nowrap">
            {article.categoryName}
          </span>
        </div>
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
          <span>{article.date}</span>
          <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
          <span>{article.readTime} 分钟阅读</span>
        </div>
        <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-background-200">
          <div className="w-6 h-6 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
            <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
          </div>
          <span className="text-xs text-foreground-500">{article.author}</span>
          <span className="ml-auto flex items-center gap-1 text-xs text-foreground-400">
            <i className="ri-thumb-up-line"></i> {article.likeCount.toLocaleString()}
          </span>
        </div>
      </div>
    </Link>
  );
}